/**
 * EcoEmpower ICT Platform - Theme Configuration
 * Version: 1.0.0
 * Last Updated: 2026-02-06
 *
 * This file provides programmatic access to theme values for JavaScript/React components.
 * All values are kept in sync with index.css CSS custom properties.
 */

export const theme = {
  colors: {
    primary: {
      dark: '#4A4A4A',        // Logo dark gray
      main: '#5C5C5C',        // Logo mid-gray
      medium: '#6B6B6B',      // Mid-tone gray
      light: '#8CB808',       // Logo lime green
      lighter: '#9FD01A',     // Lighter lime
      pale: '#EFF8D6',        // Pale lime
    },
    accent: {
      main: '#8CB808',        // Logo lime green
      dark: '#739A00',        // Darker lime
      light: '#A8D42A',       // Lighter lime
    },
    energy: {
      solar: '#FFB800',       // Solar/PV
      wind: '#4A90E2',        // Wind generation
      battery: '#9B59B6',     // Battery/storage
      grid: '#7F8C8D',        // Grid import/export
      load: '#E74C3C',        // Energy consumption
    },
    semantic: {
      success: '#2E7D32',     // Success states
      warning: '#F57C00',     // Warning states
      danger: '#C62828',      // Error/danger states
      info: '#0277BD',        // Info states
    },
    neutral: {
      900: '#1A1A1A',         // Darkest
      800: '#2D2D2D',         // Dark
      700: '#4A4A4A',         // Medium-dark
      600: '#6B6B6B',         // Medium
      500: '#8C8C8C',         // Mid-tone
      400: '#ADADAD',         // Light-medium
      300: '#CFCFCF',         // Light
      200: '#E5E5E5',         // Lighter
      100: '#F5F5F5',         // Lightest
      50: '#FAFAFA',          // Almost white
      white: '#FFFFFF',       // Pure white
    },
    earth: {
      brown: '#7B4425',       // Nutmeg Brown
      terracotta: '#C17A5F',  // Muted terracotta
      clay: '#A67B5B',        // Clay brown
      sand: '#E6D5B8',        // Sandstone beige
      moss: '#556B2F',        // Forest moss
    },
    chart: {
      primary: '#5C5C5C',
      secondary: '#8CB808',
      tertiary: '#0277BD',
      solar: '#FFB800',
      wind: '#4A90E2',
      battery: '#9B59B6',
      gridImport: '#95A5A6',
      gridExport: '#3498DB',
      load: '#E74C3C',
      background: '#FAFAFA',
      gridLine: '#CFCFCF',
      text: '#4A4A4A',
    },
  },

  typography: {
    fontFamily: {
      primary: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      mono: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Courier New', monospace",
    },
    fontSize: {
      xs: '0.75rem',      // 12px
      sm: '0.875rem',     // 14px
      base: '1rem',       // 16px
      md: '1.125rem',     // 18px
      lg: '1.25rem',      // 20px
      xl: '1.563rem',     // 25px
      '2xl': '1.953rem',  // 31px
      '3xl': '2.441rem',  // 39px
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      base: 1.5,
      relaxed: 1.75,
    },
  },

  spacing: {
    0: '0',
    1: '0.25rem',    // 4px
    2: '0.5rem',     // 8px
    3: '0.75rem',    // 12px
    4: '1rem',       // 16px
    5: '1.25rem',    // 20px
    6: '1.5rem',     // 24px
    8: '2rem',       // 32px
    10: '2.5rem',    // 40px
    12: '3rem',      // 48px
    16: '4rem',      // 64px
    20: '5rem',      // 80px
    24: '6rem',      // 96px
  },

  layout: {
    container: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
    },
    breakpoint: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
    },
  },

  borderRadius: {
    sm: '0.25rem',    // 4px
    base: '0.375rem', // 6px
    md: '0.5rem',     // 8px
    lg: '0.75rem',    // 12px
    xl: '1rem',       // 16px
    full: '9999px',   // Circular
  },

  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    base: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    md: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },

  transitions: {
    duration: {
      fast: '150ms',
      base: '250ms',
      slow: '350ms',
    },
    timing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    },
  },

  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

/**
 * Chart color palette for ApexCharts or other visualization libraries
 */
export const chartTheme = {
  colors: [
    theme.colors.chart.primary,
    theme.colors.chart.secondary,
    theme.colors.chart.tertiary,
    theme.colors.energy.solar,
    theme.colors.energy.wind,
    theme.colors.energy.battery,
    theme.colors.chart.gridImport,
    theme.colors.chart.gridExport,
  ],
  energyColors: {
    solar: theme.colors.energy.solar,
    wind: theme.colors.energy.wind,
    battery: theme.colors.energy.battery,
    grid: theme.colors.energy.grid,
    load: theme.colors.energy.load,
    gridImport: theme.colors.chart.gridImport,
    gridExport: theme.colors.chart.gridExport,
  },
  chartOptions: {
    colors: [
      theme.colors.chart.primary,
      theme.colors.chart.secondary,
      theme.colors.chart.tertiary,
    ],
    chart: {
      background: theme.colors.chart.background,
      fontFamily: theme.typography.fontFamily.primary,
    },
    grid: {
      borderColor: theme.colors.chart.gridLine,
    },
    xaxis: {
      labels: {
        style: {
          colors: theme.colors.chart.text,
          fontSize: theme.typography.fontSize.sm,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.colors.chart.text,
          fontSize: theme.typography.fontSize.sm,
        },
      },
    },
    tooltip: {
      theme: 'light',
    },
  },
};

/**
 * Utility function to get CSS custom property value
 * Usage: getCSSVar('--color-primary')
 */
export const getCSSVar = (varName) => {
  if (typeof document === 'undefined') return null;
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
};

/**
 * Utility function to set CSS custom property value
 * Usage: setCSSVar('--color-primary', '#5C5C5C')
 */
export const setCSSVar = (varName, value) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(varName, value);
};

export default theme;
